import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import DeliveryForm from '../components/DeliveryForm';
import LoadingState from '../components/LoadingState';
import { useToast } from '../context/ToastContext';
import API from '../utils/api';

export default function Deliver() {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [film, setFilm] = useState(null);
  const [delivery, setDelivery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [otherPendingCount, setOtherPendingCount] = useState(0);

  useEffect(() => {
    Promise.all([
      API.get('/api/submissions/my'),
      API.get(`/api/deliveries/${submissionId}`).catch(err => (err.response?.status === 404 ? null : Promise.reject(err))),
    ]).then(([subsRes, deliveryRes]) => {
      const all = subsRes.data.submissions || [];
      const sub = all.find(s => s.id === submissionId);
      const del = deliveryRes?.data?.delivery || null;
      if (!sub || sub.status !== 'approved' || !del || !['requested', 'qc_failed'].includes(del.delivery_status)) {
        setNotFound(true);
      } else {
        setFilm(sub);
        setDelivery(del);
        setOtherPendingCount(all.filter(s =>
          s.id !== submissionId && s.status === 'approved' && ['requested', 'qc_failed'].includes(s.delivery_status)
        ).length);
      }
    }).catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [submissionId]);

  useEffect(() => { if (notFound) navigate('/dashboard'); }, [notFound, navigate]);

  const handleSubmit = async (form) => {
    setSubmitting(true);
    try {
      await API.post(`/api/deliveries/${submissionId}`, form);
      showToast('Delivery submitted — our team will review it shortly');
      navigate('/dashboard');
    } catch (err) {
      showToast(err.response?.data?.error || 'Could not submit delivery', 'error');
    } finally { setSubmitting(false); }
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <Navbar />
      <LoadingState label="Loading delivery form…" minHeight={300} />
    </div>
  );

  if (notFound || !film || !delivery) return null;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <Navbar />
      <div style={{ maxWidth: 700, margin: '0 auto', padding: '40px 24px' }}>
        {otherPendingCount > 0 && (
          <button onClick={() => navigate('/deliver')}
            style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)', padding: 0, marginBottom: 16 }}>
            ← All deliveries ({otherPendingCount + 1})
          </button>
        )}
        <div style={{ marginBottom: 32 }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--pink)', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 10 }}>
            {delivery.delivery_status === 'qc_failed' ? 'Delivery — Needs Fixes' : 'Final Delivery'}
          </div>
          <h1 style={{ fontWeight: 900, fontSize: 30, color: 'var(--text)', marginBottom: 8, letterSpacing: '-0.02em' }}>{film.title}</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, fontWeight: 400 }}>
            Hand over your final delivery materials so we can get your film ready to publish.
          </p>
        </div>

        <DeliveryForm
          initialData={delivery}
          onSubmit={handleSubmit}
          submitting={submitting}
          submitLabel={delivery.delivery_status === 'qc_failed' ? 'Resubmit Delivery →' : 'Submit Delivery →'}
          qcNotes={delivery.delivery_status === 'qc_failed' ? delivery.qc_notes : null}
        />
      </div>
    </div>
  );
}
